import { type RefObject, useLayoutEffect, useState } from 'react';

const SCROLL_MARGIN = 32;

type UseSuggestionCardPositioningProps = {
	cardRef: RefObject<HTMLDivElement>;
	id?: string;
	root?: Element;
	targetBase?: HTMLSpanElement;
	targetEnd?: HTMLSpanElement;
	targetStart?: HTMLSpanElement;
};

type UseSuggestionCardPositioningResult = {
	anchorLeft: number | null;
	isCardBottomBelowViewport: boolean;
	isTargetAboveViewport: boolean;
	isTargetBelowViewport: boolean;
	isTargetEndAboveViewport: boolean;
	isTargetEndBelowViewport: boolean;
	isTargetRightOfViewport: boolean;
};

/**
 * Tracks the position of the suggestion card target relative to the editor viewport
 * using IntersectionObserver and ResizeObserver. Returns booleans describing where
 * the target/card sit relative to the viewport, plus the anchor's left offset used
 * to work around a Chromium bug with `anchor()` on `position: fixed` elements.
 */
export const useSuggestionCardPositioning = ({
	id,
	root,
	targetStart,
	targetBase,
	targetEnd,
	cardRef,
}: UseSuggestionCardPositioningProps): UseSuggestionCardPositioningResult => {
	const [anchorLeft, setAnchorLeft] = useState<number | null>(null);
	const [isTargetAboveViewport, setIsTargetAboveViewport] = useState(false);
	const [isTargetBelowViewport, setIsTargetBelowViewport] = useState(false);
	const [isCardBottomBelowViewport, setIsCardBottomBelowViewport] = useState(false);
	const [isTargetRightOfViewport, setIsTargetRightOfViewport] = useState(false);
	const [isTargetEndAboveViewport, setIsTargetEndAboveViewport] = useState(false);
	const [isTargetEndBelowViewport, setIsTargetEndBelowViewport] = useState(false);

	const cardHeight = cardRef.current?.offsetHeight ?? 0;
	const cardWidth = cardRef.current?.offsetWidth ?? 0;

	/**
	 * Workaround for a Chromium bug where using anchor() on a
	 * `position: fixed` element produces correct paint but incorrect hit-testing.
	 * https://issues.chromium.org/issues/512171942
	 *
	 * Only needed for when (!isTargetRightOfViewport &&
	 * (isTargetAboveViewport || isCardBottomBelowViewport || isTargetBelowViewport))
	 * as these combinations have position: fixed with non-static inline position.
	 */

	const shouldTrackAnchor =
		!isTargetRightOfViewport &&
		(isTargetAboveViewport || isCardBottomBelowViewport || isTargetBelowViewport);
	useLayoutEffect(() => {
		if (!targetBase || !shouldTrackAnchor) {
			setAnchorLeft(null);
			return;
		}

		setAnchorLeft(
			targetBase.getBoundingClientRect().left - (root?.getBoundingClientRect().left || 0),
		);

		const observer = new ResizeObserver(() => {
			setAnchorLeft(
				targetBase.getBoundingClientRect().left - (root?.getBoundingClientRect().left || 0),
			);
		});
		if (root) {
			observer.observe(root);
		}
		return () => {
			observer.disconnect();
		};
	}, [shouldTrackAnchor, targetBase, root]);

	useLayoutEffect(() => {
		if (!id || !root || !targetStart || !targetEnd || !cardRef.current || !targetBase) {
			setIsTargetRightOfViewport(false);
			setIsTargetEndAboveViewport(false);
			setIsTargetEndBelowViewport(false);
			setIsTargetBelowViewport(false);
			setIsTargetAboveViewport(false);
			setIsCardBottomBelowViewport(false);
			return;
		}

		const verticalCardPositionObserver = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				if (!entry || !entry.rootBounds) {
					return;
				}
				const { rootBounds, boundingClientRect } = entry;
				setIsTargetAboveViewport(boundingClientRect.top < rootBounds.top);
				setIsCardBottomBelowViewport(boundingClientRect.bottom > rootBounds.bottom);
			},
			{
				root,
				rootMargin: `-${SCROLL_MARGIN}px 0px -${cardHeight + SCROLL_MARGIN}px 0px`,
			},
		);

		const verticalTargetObserver = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				if (!entry || !entry.rootBounds) {
					return;
				}
				const { rootBounds, boundingClientRect } = entry;

				setIsTargetBelowViewport(boundingClientRect.top > rootBounds.bottom);
			},
			{
				root,
				rootMargin: `${cardHeight + SCROLL_MARGIN}px 0px -${SCROLL_MARGIN}px 0px`,
			},
		);

		const targetEndObserver = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				if (!entry || !entry.rootBounds) {
					return;
				}
				const { rootBounds, boundingClientRect } = entry;
				setIsTargetEndAboveViewport(boundingClientRect.top < rootBounds.top);
				setIsTargetEndBelowViewport(boundingClientRect.bottom > rootBounds.bottom);
			},
			{
				root,
				// Extra 24 for the gap above card and content
				rootMargin: `0px 0px -${cardHeight + SCROLL_MARGIN + 24}px 0px`,
			},
		);

		/**
		 * This observer is for determining if there's enough space on the right side to display the card.
		 * We use arbitrarily large root margins to ensure that the intersection observer detects it even
		 * when the targetBase anchor (anchored to the icon button) is off the screen.
		 *
		 * A hypotheical edge case is if a toolcall's localid position is more than 99999px further than the
		 * doc-level node's local id, but that is really unlikely.
		 */
		const horizontalObserver = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				if (!entry || !entry.rootBounds) {
					return;
				}
				const { rootBounds, boundingClientRect } = entry;
				setIsTargetRightOfViewport(boundingClientRect.right > rootBounds.right);
			},
			{
				root,
				rootMargin: `99999px -${cardWidth + SCROLL_MARGIN}px 99999px 0px`,
			},
		);

		verticalCardPositionObserver.observe(targetStart);
		horizontalObserver.observe(targetBase);
		targetEndObserver.observe(targetEnd);
		verticalTargetObserver.observe(targetStart);
		return () => {
			verticalCardPositionObserver.disconnect();
			horizontalObserver.disconnect();
			targetEndObserver.disconnect();
			verticalTargetObserver.disconnect();
		};
	}, [targetStart, targetBase, cardRef, root, targetEnd, cardHeight, cardWidth, id]);

	return {
		anchorLeft,
		isTargetAboveViewport,
		isTargetBelowViewport,
		isCardBottomBelowViewport,
		isTargetRightOfViewport,
		isTargetEndAboveViewport,
		isTargetEndBelowViewport,
	};
};
